<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="product_list" />
    
    <div class="card border-0 shadow-none rounded-1 mb-25">
      <div class="card-body p-xl-40">
        <div class="product-toolbar mb-4">
          <div class="product-toolbar__filters">
            <div class="product-toolbar__filter product-toolbar__filter--search">
              <input
                type="text"
                class="form-control product-toolbar__control"
                :placeholder="t('product_page.search_by_name_or_code')"
                v-model="searchQuery"
              />
            </div>
            <div class="product-toolbar__filter">
              <select
                class="form-select product-toolbar__control"
                v-model.number="selectedWarehouseId"
                :disabled="warehouseLockedToProfile"
              >
                <option :value="0">{{ t("product_page.all_warehouses") }}</option>
                <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
              </select>
            </div>
            <div class="product-toolbar__filter">
              <select class="form-select product-toolbar__control" v-model.number="selectedCategoryId">
                <option :value="0">{{ t("product_page.all_categories") }}</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="product-toolbar__filter">
              <select class="form-select product-toolbar__control" v-model.number="selectedBrandId">
                <option :value="0">{{ t("product_page.all_brands") }}</option>
                <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
            <div class="product-toolbar__filter">
              <select class="form-select product-toolbar__control" v-model.number="selectedUnitId">
                <option :value="0">{{ t("product_page.all_units") }}</option>
                <option v-for="u in units" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
            <div class="product-toolbar__filter">
              <select class="form-select product-toolbar__control" v-model.number="selectedSizeId">
                <option :value="0">{{ t("product_page.all_sizes") }}</option>
                <option v-for="s in sizes" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
          </div>

          <div class="product-toolbar__actions">
              <button
                class="btn style-three action-btn d-none"
                :disabled="!selectedRowKeys.length"
                @click="exportExcel"
              >
                Excel
              </button>
              <button class="btn style-five action-btn" @click="triggerImport">
                {{ t("product_page.import_button") }}
              </button>
              <router-link to="/create-product" class="btn style-one action-btn">
                {{ t("product_page.create_button") }}
                <img src="../../assets/img/icons/add-circle.svg" :alt="t('product_page.add')" />
              </router-link>
              <input
                ref="importInput"
                type="file"
                class="d-none"
                @change="handleImportChange"
              />
          </div>
        </div>

        <div class="product-table-shell">
          <a-table
            :columns="columns"
            :data-source="filteredProducts"
            :row-key="'id'"
            :loading="loading"
            :pagination="paginationConfig"
            :locale="tableLocale"
            :row-selection="rowSelection"
            :scroll="{ x: 1220 }"
            @change="handleTableChange"
            class="text-nowrap align-middle product-table"
          >
            <template #bodyCell="{ column, record, text }">
              <template v-if="column.key === 'image'">
                <img
                  :src="getImageUrl(record)"
                  :alt="t('product_page.product_image')"
                  style="width: 40px; height: 30px; object-fit: contain; background: #f8f9fa;"
                  class="rounded-1"
                />
              </template>
              <template v-else-if="column.key === 'price'">
                {{ stateStore.formatPrice(Number(record.price || 0)) }}
              </template>
              <template v-else-if="column.key === 'category'">
                {{ record.category?.name || t("product_page.not_available") }}
              </template>
              <template v-else-if="column.key === 'brand'">
                {{ record.brand?.name || t("product_page.not_available") }}
              </template>
              <template v-else-if="column.key === 'unit'">
                {{ record.unit?.name || t("product_page.not_available") }}
              </template>
              <template v-else-if="column.key === 'size'">
                {{ getSizeLabel(record) }}
              </template>
              <template v-else-if="column.key === 'type'">
                <span :class="record.type === 2 ? 'badge bg-info' : 'badge bg-primary'">
                  {{ record.type === 2 ? t("product_page.service") : t("product_page.standard") }}
                </span>
              </template>
              <template v-else-if="column.key === 'stock'">
                {{ getDisplayQty(record) }}
              </template>
              <template v-else-if="column.key === 'emenu'">
                <label class="table-emenu-toggle" :class="{ 'is-enabled': record.show_on_emenu }">
                  <input
                    type="checkbox"
                    :checked="Boolean(record.show_on_emenu)"
                    @change="handleEmenuToggle(record, $event)"
                  />
                  <span class="table-emenu-toggle__switch" aria-hidden="true"></span>
                  <span class="table-emenu-toggle__label">
                    {{ record.show_on_emenu ? t("product_page.enabled") : t("product_page.disabled") }}
                  </span>
                </label>
              </template>

              <template v-else-if="column.key === 'actions'">
                <div class="d-flex justify-content-center gap-2">
                  <router-link :to="`/product-details?id=${record.id}`" class="btn p-0 border-0" :title="t('product_page.view')">
                    <img src="../../assets/img/icons/eye.svg" :alt="t('product_page.view')" style="width: 14px" />
                  </router-link>
                  <router-link :to="`/edit-product?id=${record.id}`" class="btn p-0 border-0" :title="t('product_page.edit')">
                    <img src="../../assets/img/icons/edit.svg" :alt="t('product_page.edit')" style="width: 14px" />
                  </router-link>
                  <button
                    class="btn p-0 border-0"
                    :title="t('product_page.delete')"
                    @click="deleteProduct(record)"
                  >
                    <img src="../../assets/img/icons/close.svg" :alt="t('product_page.delete')" style="width: 14px" />
                  </button>
                </div>
              </template>
              <template v-else>
                {{ text }}
              </template>
            </template>
          </a-table>
        </div>
      </div>
    </div>

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useProduct } from "../../composables/useProduct";
import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";

const { t } = useI18n();

const {
  filteredProducts,
  loading,
  importInput,
  searchQuery,
  selectedCategoryId,
  selectedBrandId,
  selectedUnitId,
  selectedSizeId,
  selectedWarehouseId,
  warehouseLockedToProfile,
  categories,
  brands,
  units,
  sizes,
  warehouses,
  selectedIds,
  paginationConfig,
  stateStore,
  fetchProducts,
  fetchFiltersData,
  initializeWarehouseContext,
  deleteProduct,
  toggleEmenuVisibility,
  getImageUrl,
  getDisplayQty,
  getSizeLabel,
  exportExcel,
  triggerImport,
  handleImportChange,
  handleTableChange,
} = useProduct();

onMounted(async () => {
  await initializeWarehouseContext();
  await Promise.all([fetchProducts(), fetchFiltersData()]);
});

// Row Selection Logic for Ant Design Table
const selectedRowKeys = computed({
  get: () => selectedIds.value,
  set: (val) => { selectedIds.value = val; }
});

const rowSelection = {
  selectedRowKeys: selectedRowKeys,
  onChange: (newSelectedRowKeys: (string | number)[]) => {
    selectedRowKeys.value = newSelectedRowKeys as number[];
  },
};

const handleEmenuToggle = (record: any, event: Event) => {
  const target = event.target as HTMLInputElement;
  toggleEmenuVisibility(record, target.checked);
};

// Sync underlying selection logic
watch(selectedRowKeys, (newVal) => {
  selectedIds.value = newVal;
});

const tableLocale = computed(() => ({
  emptyText: t("product_page.no_products_found"),
}));

const columns = computed(() => [
  { title: t("product_page.image"), dataIndex: "image", key: "image", align: "center" },
  { title: t("product_page.code"), dataIndex: "code", key: "code", align: "center", sorter: (a: any, b: any) => a.code.localeCompare(b.code) },
  { title: t("product_page.product_name"), dataIndex: "name", key: "name", align: "center", sorter: (a: any, b: any) => a.name.localeCompare(b.name) },
  { title: t("product_page.category"), dataIndex: "category", key: "category", align: "center" },
  { title: t("product_page.brand"), dataIndex: "brand", key: "brand", align: "center" },
  { title: t("product_page.price"), dataIndex: "price", key: "price", align: "center", sorter: (a: any, b: any) => Number(a.price) - Number(b.price) },
  { title: t("product_page.unit"), dataIndex: "unit", key: "unit", align: "center" },
  { title: t("product_page.size"), dataIndex: "size", key: "size", align: "center" },
  { title: t("product_page.type"), dataIndex: "type", key: "type", align: "center" },
  { title: t("product_page.stock"), key: "stock", align: "center", sorter: (a: any, b: any) => Number(getDisplayQty(a)) - Number(getDisplayQty(b)) },
  { title: t("product_page.public_emenu"), key: "emenu", align: "center" },
  { title: t("product_page.action"), key: "actions", align: "center" },
]);
</script>

<style scoped>
.action-btn {
  min-width: 110px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
}

.product-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
}

.product-toolbar__filters {
  display: grid;
  flex: 1 1 760px;
  grid-template-columns: minmax(220px, 1.4fr) repeat(5, minmax(140px, 1fr));
  gap: 12px;
}

.product-toolbar__filter {
  min-width: 0;
}

.product-toolbar__filter--search {
  grid-column: span 1;
}

.product-toolbar__control {
  min-height: 44px;
}

.product-toolbar__actions {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.table-emenu-toggle {
  align-items: center;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  min-width: 112px;
  padding: 5px 10px 5px 6px;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.table-emenu-toggle input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.table-emenu-toggle__switch {
  background: #94a3b8;
  border-radius: 999px;
  height: 20px;
  position: relative;
  transition: background 0.2s ease;
  width: 38px;
}

.table-emenu-toggle__switch::after {
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.22);
  content: "";
  height: 16px;
  left: 2px;
  position: absolute;
  top: 2px;
  transition: transform 0.2s ease;
  width: 16px;
}

.table-emenu-toggle__label {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  min-width: 50px;
  text-align: left;
}

.table-emenu-toggle.is-enabled {
  background: #ecfdf3;
  border-color: #bbf7d0;
}

.table-emenu-toggle.is-enabled .table-emenu-toggle__switch {
  background: #16a34a;
}

.table-emenu-toggle.is-enabled .table-emenu-toggle__switch::after {
  transform: translateX(18px);
}

.table-emenu-toggle.is-enabled .table-emenu-toggle__label {
  color: #15803d;
}

.table-emenu-toggle input:focus-visible + .table-emenu-toggle__switch {
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.18);
}

.product-table-shell {
  overflow-x: auto;
}

.product-table-shell :deep(.ant-table-wrapper) {
  min-width: 100%;
}

@media (max-width: 1399px) {
  .product-toolbar__filters {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
  }
}

@media (max-width: 991px) {
  .product-toolbar__filters {
    grid-template-columns: repeat(2, minmax(170px, 1fr));
    flex-basis: 100%;
  }

  .product-toolbar__actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 575px) {
  .product-toolbar__filters {
    grid-template-columns: minmax(0, 1fr);
  }

  .product-toolbar__actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }
}

.chart-wrapper {
  margin: 0 -10px;
  .card {
    margin-left: 10px;
    margin-right: 10px;
  }
}
</style>
